import { type Constraint, type ConstraintId, asConstraintId } from '@/domain/constraints';
import { type EntitySource } from '@/domain/construction';
import { type Vec2 } from '@/domain/math';
import {
  asEntityId,
  createDocument,
  DEFAULT_STYLE,
  type Entity,
  type EntityId,
  type SceneDocument,
  type Style,
} from '@/domain/model';

import {
  CURRENT_SCHEMA_VERSION,
  FORMAT_TAG,
  type WireConstraint,
  type WireDocumentV2,
  type WireEntity,
  type WireStyle,
} from './schema/v2';

/**
 * Encoding and decoding.
 *
 * ## Decoding never trusts its input
 *
 * A file may be hand-edited, truncated by a failed upload, or produced by a build that
 * did not exist when this one was written. `decode` validates everything and reports
 * precise errors rather than producing a half-built document that fails much later with
 * an unrelated symptom.
 *
 * The failure mode it is most careful about is the quiet one: a dangling parent
 * reference produces an entity that simply never updates. That is checked here, at load,
 * where the message can say which entity and which parent.
 */

export class DecodeError extends Error {
  readonly path: string;

  constructor(message: string, path: string) {
    super(path === '' ? message : `${message} (at ${path})`);
    this.name = 'DecodeError';
    this.path = path;
  }
}

/**
 * A decoded file, plus everything the codec did not understand.
 *
 * `unknown` carries top-level fields written by a newer build. Handing them back on the
 * next `encode` is what makes the format forward compatible: a file that passes through
 * an older version does not silently lose the data that version knew nothing about.
 */
export interface DecodedDocument {
  readonly document: SceneDocument;
  readonly unknown: Readonly<Record<string, unknown>>;
  /** Non-fatal problems worth surfacing, such as an entity dropped as unreadable. */
  readonly warnings: readonly string[];
}

/* ------------------------------------------------------------------- encoding */

/**
 * Normalises `-0` to `0`.
 *
 * IEEE negative zero is arithmetically identical to zero and the geometry kernel
 * deliberately does not spend an operation per vector write to remove it (see
 * `Vec2.test.ts`). It becomes observable exactly here: `JSON.stringify(-0)` is `"0"`, so
 * a round trip would silently change the value and break the losslessness test. This is
 * the one place it costs nothing to fix.
 */
function num(value: number): number {
  return value === 0 ? 0 : value;
}

function encodeVec(v: Vec2): readonly [number, number] {
  return [num(v.x), num(v.y)];
}

function encodeStyle(style: Style): WireStyle {
  return {
    stroke: style.stroke,
    strokeWidth: num(style.strokeWidth),
    lineStyle: style.lineStyle,
    fill: style.fill,
    fillStyle: style.fillStyle,
    opacity: num(style.opacity),
    pointStyle: style.pointStyle,
    pointSize: num(style.pointSize),
    arrows: style.arrows,
  };
}

/** Recursively rewrites `{x, y}` into `[x, y]` inside a construction source. */
function encodeSource(source: EntitySource): Readonly<Record<string, unknown>> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    result[key] = encodeValue(value);
  }
  return result;
}

function encodeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(encodeValue);
  if (isVec2(value)) return encodeVec(value);
  if (typeof value === 'number') return num(value);
  return value;
}

function isVec2(value: unknown): value is Vec2 {
  if (typeof value !== 'object' || value === null) return false;
  const keys = Object.keys(value);
  if (keys.length !== 2) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate['x'] === 'number' && typeof candidate['y'] === 'number';
}

export interface EncodeOptions {
  /** Unknown fields from a newer writer, handed back so nothing is lost. */
  readonly preserve?: Readonly<Record<string, unknown>> | undefined;
}

export function encode(document: SceneDocument, options: EncodeOptions = {}): WireDocumentV2 {
  // Style palette: intern by structural key so identical styles share one entry.
  const styles: WireStyle[] = [];
  const styleIndex = new Map<string, number>();

  const internStyle = (style: Style): number => {
    const wire = encodeStyle(style);
    const key = JSON.stringify(wire);
    const existing = styleIndex.get(key);
    if (existing !== undefined) return existing;
    const index = styles.length;
    styles.push(wire);
    styleIndex.set(key, index);
    return index;
  };

  const entities: WireEntity[] = [];
  for (const entity of document.entities.values()) {
    entities.push({
      id: entity.id,
      source: encodeSource(entity.source),
      style: internStyle(entity.style),
      // Defaults are omitted; the decoder restores them.
      ...(entity.zIndex === 0 ? {} : { z: num(entity.zIndex) }),
      ...(entity.visible ? {} : { visible: false }),
      ...(entity.locked ? { locked: true } : {}),
      ...(entity.isConstruction ? { construction: true } : {}),
      ...(entity.label === undefined ? {} : { label: entity.label }),
    });
  }

  const constraints: WireConstraint[] = [];
  for (const constraint of document.constraints.values()) {
    const wire: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(constraint)) {
      wire[key] = encodeValue(value);
    }
    constraints.push(wire as unknown as WireConstraint);
  }

  return {
    ...options.preserve,
    format: FORMAT_TAG,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    title: document.title,
    metadata: { ...document.metadata },
    nextId: document.nextId,
    styles,
    entities,
    constraints,
  };
}

export function encodeToJson(document: SceneDocument, options: EncodeOptions = {}): string {
  return JSON.stringify(encode(document, options), null, 2);
}

/* ------------------------------------------------------------------- decoding */

const KNOWN_TOP_LEVEL = new Set([
  'format',
  'schemaVersion',
  'title',
  'metadata',
  'nextId',
  'styles',
  'entities',
  'constraints',
]);

function asRecord(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new DecodeError('expected an object', path);
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new DecodeError('expected an array', path);
  return value;
}

function asString(value: unknown, path: string): string {
  if (typeof value !== 'string') throw new DecodeError('expected a string', path);
  return value;
}

function asFiniteNumber(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new DecodeError('expected a finite number', path);
  }
  return value;
}

function decodeVec(value: unknown, path: string): Vec2 {
  const pair = asArray(value, path);
  if (pair.length !== 2) throw new DecodeError('expected [x, y]', path);
  return { x: asFiniteNumber(pair[0], `${path}[0]`), y: asFiniteNumber(pair[1], `${path}[1]`) };
}

/** Rewrites `[x, y]` back into `{x, y}` wherever a two-number array appears. */
function decodeValue(value: unknown, path: string): unknown {
  if (Array.isArray(value)) {
    if (value.length === 2 && value.every((item) => typeof item === 'number')) {
      return decodeVec(value, path);
    }
    return value.map((item, index) => decodeValue(item, `${path}[${String(index)}]`));
  }
  return value;
}

function decodeStyle(value: unknown, path: string): Style {
  const raw = asRecord(value, path);
  return {
    stroke: typeof raw['stroke'] === 'string' ? raw['stroke'] : DEFAULT_STYLE.stroke,
    strokeWidth:
      typeof raw['strokeWidth'] === 'number' ? raw['strokeWidth'] : DEFAULT_STYLE.strokeWidth,
    lineStyle: (typeof raw['lineStyle'] === 'string'
      ? raw['lineStyle']
      : DEFAULT_STYLE.lineStyle) as Style['lineStyle'],
    fill: typeof raw['fill'] === 'string' ? raw['fill'] : DEFAULT_STYLE.fill,
    fillStyle: (typeof raw['fillStyle'] === 'string'
      ? raw['fillStyle']
      : DEFAULT_STYLE.fillStyle) as Style['fillStyle'],
    opacity: typeof raw['opacity'] === 'number' ? raw['opacity'] : DEFAULT_STYLE.opacity,
    pointStyle: (typeof raw['pointStyle'] === 'string'
      ? raw['pointStyle']
      : DEFAULT_STYLE.pointStyle) as Style['pointStyle'],
    pointSize: typeof raw['pointSize'] === 'number' ? raw['pointSize'] : DEFAULT_STYLE.pointSize,
    /*
     * Defaulted like every other field, even though the migration writes it.
     *
     * The two are not the same guarantee: the migration fixes files on the way in, and this
     * catches a style object that is malformed for some other reason. A decoder that trusted the
     * migration would hand `undefined` to the painter, which draws nothing and says nothing.
     */
    arrows: (typeof raw['arrows'] === 'string'
      ? raw['arrows']
      : DEFAULT_STYLE.arrows) as Style['arrows'],
  };
}

/**
 * Decodes a document that has already been migrated to the current schema version.
 *
 * Entities that cannot be read are dropped with a warning rather than failing the whole
 * load: recovering nine hundred and ninety-nine entities from a file with one corrupt
 * record is far better for the user than recovering none. A structurally invalid file —
 * wrong format tag, entities not an array — still fails outright, because guessing at
 * that point would produce a document nobody asked for.
 */
export function decode(raw: unknown): DecodedDocument {
  const root = asRecord(raw, '');

  if (root['format'] !== FORMAT_TAG) {
    throw new DecodeError(`not an EduSketch file (format is ${String(root['format'])})`, '');
  }

  const warnings: string[] = [];

  const styles = asArray(root['styles'] ?? [], 'styles').map((style, index) =>
    decodeStyle(style, `styles[${String(index)}]`)
  );

  const entities = new Map<EntityId, Entity>();
  const wireEntities = asArray(root['entities'] ?? [], 'entities');

  wireEntities.forEach((value, index) => {
    const path = `entities[${String(index)}]`;
    try {
      const raw2 = asRecord(value, path);
      const id = asEntityId(asString(raw2['id'], `${path}.id`));

      const sourceRaw = asRecord(raw2['source'], `${path}.source`);
      if (typeof sourceRaw['op'] !== 'string') {
        throw new DecodeError('source has no op', `${path}.source`);
      }

      const source: Record<string, unknown> = {};
      for (const [key, field] of Object.entries(sourceRaw)) {
        source[key] = decodeValue(field, `${path}.source.${key}`);
      }

      const styleIndex = typeof raw2['style'] === 'number' ? raw2['style'] : -1;
      const style = styles[styleIndex] ?? DEFAULT_STYLE;

      const label = raw2['label'];
      entities.set(id, {
        id,
        source: source as unknown as EntitySource,
        style,
        zIndex: typeof raw2['z'] === 'number' ? raw2['z'] : 0,
        visible: raw2['visible'] !== false,
        locked: raw2['locked'] === true,
        isConstruction: raw2['construction'] === true,
        ...(typeof label === 'string' ? { label } : {}),
      });
    } catch (error) {
      warnings.push(`dropped ${path}: ${error instanceof Error ? error.message : String(error)}`);
    }
  });

  const constraints = new Map<ConstraintId, Constraint>();
  asArray(root['constraints'] ?? [], 'constraints').forEach((value, index) => {
    const path = `constraints[${String(index)}]`;
    try {
      const raw2 = asRecord(value, path);
      const id = asConstraintId(asString(raw2['id'], `${path}.id`));
      if (typeof raw2['kind'] !== 'string') {
        throw new DecodeError('constraint has no kind', path);
      }
      const decoded: Record<string, unknown> = {};
      for (const [key, field] of Object.entries(raw2)) {
        decoded[key] = decodeValue(field, `${path}.${key}`);
      }
      constraints.set(id, decoded as unknown as Constraint);
    } catch (error) {
      warnings.push(`dropped ${path}: ${error instanceof Error ? error.message : String(error)}`);
    }
  });

  const metadata: Record<string, string> = {};
  if (root['metadata'] !== undefined) {
    for (const [key, value] of Object.entries(asRecord(root['metadata'], 'metadata'))) {
      if (typeof value === 'string') metadata[key] = value;
    }
  }

  const unknown: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(root)) {
    if (!KNOWN_TOP_LEVEL.has(key)) unknown[key] = value;
  }

  const base = createDocument(typeof root['title'] === 'string' ? root['title'] : 'Untitled');
  const nextId =
    typeof root['nextId'] === 'number' && Number.isFinite(root['nextId'])
      ? root['nextId']
      : highestId(entities) + 1;

  return {
    document: { ...base, entities, constraints, metadata, nextId },
    unknown,
    warnings,
  };
}

export function decodeFromJson(text: string): DecodedDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new DecodeError(
      `not valid JSON: ${error instanceof Error ? error.message : String(error)}`,
      ''
    );
  }
  return decode(parsed);
}

/**
 * Highest numeric suffix across the entity ids.
 *
 * The fallback when a file has no `nextId`. Without it a hand-written file would start
 * allocating ids that already exist, and the first new entity would silently replace one.
 */
function highestId(entities: ReadonlyMap<EntityId, Entity>): number {
  let highest = 0;
  for (const id of entities.keys()) {
    const match = /(\d+)$/.exec(id);
    const value = match?.[1] === undefined ? 0 : Number.parseInt(match[1], 10);
    if (value > highest) highest = value;
  }
  return highest;
}
